// hooks/use-action-flow.ts
import { useCallback } from 'react';
import { useMessage } from '@/shared/hooks/useMessage';
import { useActionFlowStore } from '../stores/action-flow-store';
import {
    useGetAllActionFlowConfigurations,
    useGetActionFlowConfiguration,
    useCreateActionFlowConfiguration,
    useUpdateActionFlowConfiguration,
    useDeleteActionFlowConfiguration, GetActionFlowConfigurationQueryResult, GetAllActionFlowConfigurationsQueryResult
} from '@/shared/api/client/action-flow-configuration';
import {
    useGetAllSubscriptions,
    useGetSubscriptionsByDomain,
    useAssignFlow,
    GetSubscriptionsByDomainQueryResult,
    GetAllSubscriptionsQueryResult,
     useGetDynamicEditFormConfig, useGetDynamicRuntimeFormConfig, useGetDynamicRuntimeFormConfigByNodeId
} from '@/shared/api/client/action-flow-subscription';
import type { ActionFlowConfigurationDTO, ActionFlowSubscriptionDTO } from '@/shared/api/model';
import {useSetDynamicFormConfig} from "@/shared/api/client/action-flow-subscription";


export const useActionFlowConfigurations = (filter: Record<string, any> = {}) => {
    const store = useActionFlowStore();
    const message = useMessage();

    const { data, isLoading, error } = useGetAllActionFlowConfigurations(
        // @ts-ignore
        { ...filter },
        {
            query: {
                onSuccess: (response: GetAllActionFlowConfigurationsQueryResult) => {
                    store.setConfigurations(response);
                },
                // @ts-ignore
                onError: (error) => {
                    store.setError(error);
                    message.error('Failed to fetch configurations');
                }
            }
        }
    );

    return {
        configurations: data ?? store.configurations,
        isLoading,
        error
    };
};

export const useActionFlowConfiguration = (id: string) => {
    const store = useActionFlowStore();
    const message = useMessage();

    const { data, isLoading } = useGetActionFlowConfiguration(
        id,
        {
            query: {
                enabled: !!id,
                // @ts-ignore
                onSuccess: (response: GetConfigurationQueryResult) => {
                    store.setActiveConfiguration(response);
                },
                // @ts-ignore
                onError: (error) => {
                    store.setError(error);
                    message.error('Failed to fetch configuration');
                }
            }
        }
    );

    return {
        configuration: data ?? store.activeConfiguration,
        isLoading
    };
};
export const useManageConfiguration = () => {
    const store = useActionFlowStore();
    const message = useMessage();
    const createMutation = useCreateActionFlowConfiguration();
    const updateMutation = useUpdateActionFlowConfiguration();
    const deleteMutation = useDeleteActionFlowConfiguration();

    const createConfiguration = useCallback(async (data: ActionFlowConfigurationDTO) => {
        try {
            const response = await createMutation.mutateAsync({ data });
            store.addConfiguration(response);
            message.success('Configuration created successfully');
            return response;
        } catch (error) {
            message.error('Failed to create configuration');
            throw error;
        }
    }, [createMutation, store, message]);

    const updateConfiguration = useCallback(async (id: string, data: ActionFlowConfigurationDTO) => {
        try {
            const response = await updateMutation.mutateAsync({ id, data });
            store.updateConfiguration(response);
            message.success('Configuration updated successfully');
            return response;
        } catch (error) {
            message.error('Failed to update configuration');
            throw error;
        }
    }, [updateMutation, store, message]);

    const deleteConfiguration = useCallback(async (id: string) => {
        try {
            await deleteMutation.mutateAsync({ id });
            store.removeConfiguration(id);
            message.success('Configuration deleted successfully');
        } catch (error) {
            message.error('Failed to delete configuration');
            throw error;
        }
    }, [deleteMutation, store, message]);

    return {
        createConfiguration,
        updateConfiguration,
        deleteConfiguration,
        isLoading: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
    };
};
export const useActionFlowSubscriptions = () => {
    const store = useActionFlowStore();
    const message = useMessage();

    const { data, isLoading, error, refetch } = useGetAllSubscriptions({
        query: {
            enabled: true,
            // @ts-ignore
            onSuccess: (response: GetAllSubscriptionsQueryResult) => {
                store.setSubscriptions(response);
            },
            // @ts-ignore
            onError: (error) => {
                store.setError(error);
                message.error('Failed to fetch subscriptions');
            }
        }
    });

    return {
        subscriptions: data ?? store.subscriptions,
        isLoading,
        error,
        refetch
    };
};

export const useActionFlowSubscriptionsByDomain = (domain: string) => {
    const store = useActionFlowStore();
    const message = useMessage();

    const { data, isLoading, error } = useGetSubscriptionsByDomain(domain, {
        query: {
            enabled: !!domain,
            // @ts-ignore
            onSuccess: (response: GetSubscriptionsByDomainQueryResult) => {
                store.setSubscriptions(response);
            },
            // @ts-ignore
            onError: (error) => {
                store.setError(error);
                message.error('Failed to fetch domain subscriptions');
            }
        }
    });

    return {
        subscriptions: data ?? store.subscriptions,
        isLoading,
        error,
        refetch: () => {}
    };
};

export const useManageSubscription = () => {
    const store = useActionFlowStore();
    const message = useMessage();
    const assignFlowMutation = useAssignFlow();
    const setFormConfigMutation = useSetDynamicFormConfig();

    const assignFlow = useCallback(async (params: { domain: string; subscriptionId: string; flowId: string }) => {
        try {
            const response = await assignFlowMutation.mutateAsync({
                domain: params.domain,
                subscriptionId: params.subscriptionId,
                flowId: params.flowId
            });
            message.success('Flow assigned successfully');
            return response;
        } catch (error) {
            message.error('Failed to assign flow');
            throw error;
        }
    }, [assignFlowMutation, message]);

    const setFormConfig = useCallback(async (params: { domain: string; subscriptionId: string; data: string }) => {
        try {
            await setFormConfigMutation.mutateAsync({
                domain: params.domain,
                subscriptionId: params.subscriptionId,
                data: params.data
            });
            // Updated to use new store method with mode parameter
            store.setFormConfig('edit', params.domain, params.subscriptionId, params.data);
            message.success('Form configuration updated successfully');
        } catch (error) {
            message.error('Failed to update form configuration');
            throw error;
        }
    }, [setFormConfigMutation, store, message]);

    return {
        assignFlow,
        setFormConfig,
        isLoading: assignFlowMutation.isPending || setFormConfigMutation.isPending
    };
};

export const useDynamicEditFormConfig = (domain: string, subscriptionId: string) => {
    const store = useActionFlowStore();
    const message = useMessage();

    const {
        data,
        isLoading,
        isError,
        error
    } = useGetDynamicEditFormConfig(domain, subscriptionId, {
        query: {
            enabled: !!(domain && subscriptionId),
            // @ts-ignore
            onSuccess: (response: GetDynamicFormConfigQueryResult) => {
                // Updated to use new store method with 'edit' mode
                store.setFormConfig('edit', domain, subscriptionId, response);
            },
            // @ts-ignore
            onError: (error) => {
                store.setError(error);
                message.error('Failed to fetch form configuration');
            }
        }
    });

    return {
        // Updated to use new store method with 'edit' mode
        config: data ?? store.getFormConfig('edit', domain, subscriptionId),
        isLoading,
        isError,
        error
    };
};

export const useDynamicRuntimeFormConfig = (domain: string, subscriptionId: string, nodeId?: string) => {
    const store = useActionFlowStore();
    const message = useMessage();

    // Use nodeId-based config fetch if nodeId is provided
    if (nodeId) {
        const {
            data,
            isLoading,
            isError,
            error
        } = useGetDynamicRuntimeFormConfigByNodeId(domain, subscriptionId, nodeId, {
            query: {
                enabled: !!(domain && subscriptionId && nodeId),
                // @ts-ignore
                onSuccess: (response) => {
                    // Store the data in the store
                    store.setFormConfig('runtime', domain, subscriptionId, response);
                },
                // @ts-ignore
                onError: (error) => {
                    store.setError(error);
                    message.error('Failed to fetch form configuration for node');
                }
            }
        });

        return {
            config: data ?? store.getFormConfig('runtime', domain, subscriptionId),
            isLoading,
            isError,
            error
        };
    }

    // Fallback to original implementation if no nodeId
    const {
        data,
        isLoading,
        isError,
        error
    } = useGetDynamicRuntimeFormConfig(domain, subscriptionId, {
        query: {
            enabled: !!(domain && subscriptionId),
            // @ts-ignore
            onSuccess: (response) => {
                // Updated to use new store method with 'runtime' mode
                store.setFormConfig('runtime', domain, subscriptionId, response);
            },
            // @ts-ignore
            onError: (error) => {
                store.setError(error);
                message.error('Failed to fetch form configuration');
            }
        }
    });
    return {
        config: data ?? store.getFormConfig('runtime', domain, subscriptionId),
        isLoading,
        isError,
        error
    };
};

// Optional: Add a new hook for easier form config access
export const useFormConfigurationAccess = (mode: 'runtime' | 'edit', domain: string, subscriptionId: string) => {
    const config = useActionFlowStore(state => state.formConfigs[mode][`${domain}:${subscriptionId}`]);
    return config;
};